import { Filter, Plus, Search } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ServiceType } from '@/api/types/common';
import { type OrderStatusFilter, ORDER_SHIPMENT_STATUS_NEW } from '@/api/types/order-shipments';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  FILTER_ANY_VALUE,
  filterFieldLabelClasses,
  filterGridClasses,
  filterInputClasses,
  filterSearchButtonClasses,
  filterSelectTriggerClasses,
  filterSubmitButtonClasses,
} from '@/lib/filters';
import { ORDER_STATUS_FILTERS } from '@/lib/tracking-event-codes';
import { cn } from '@/lib/utils';

import { DetailedStatusCombobox } from './DetailedStatusCombobox';

export type ShipmentsFilterValues = {
  service_type: ServiceType | null;
  status: OrderStatusFilter | null;
  event_code: string | null;
  tracking_number: string;
  reference: string;
  tag: string;
  order_number: string;
  email: string;
  phone: string;
  missing_point: boolean;
  date_from: string;
  date_to: string;
  shipment_date_from: string;
  shipment_date_to: string;
};

export const EMPTY_FILTER_VALUES: ShipmentsFilterValues = {
  service_type: null,
  status: null,
  event_code: null,
  tracking_number: '',
  reference: '',
  tag: '',
  order_number: '',
  email: '',
  phone: '',
  missing_point: false,
  date_from: '',
  date_to: '',
  shipment_date_from: '',
  shipment_date_to: '',
};

const SERVICE_TYPES: ServiceType[] = ['locker', 'pudo', 'to_door'];

const fieldLabelClasses = filterFieldLabelClasses;
const selectTriggerClasses = filterSelectTriggerClasses;
const inputClasses = filterInputClasses;
const searchButtonClasses = filterSearchButtonClasses;

function SearchInputGroup({
  label,
  value,
  onChange,
  onSubmit,
  ariaLabel,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  ariaLabel: string;
}) {
  return (
    <div className="flex flex-col gap-3">
      <span className={fieldLabelClasses}>{label}</span>
      <div className="flex">
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onSubmit();
          }}
          className={inputClasses}
        />
        <button
          type="button"
          onClick={onSubmit}
          aria-label={ariaLabel}
          className={searchButtonClasses}
        >
          <Search className="size-4" />
        </button>
      </div>
    </div>
  );
}

function TextFilterGroup({
  label,
  value,
  onChange,
  onSubmit,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  placeholder?: string;
}) {
  return (
    <div className="flex flex-col gap-3">
      <span className={fieldLabelClasses}>{label}</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onSubmit();
        }}
        placeholder={placeholder}
        className={cn(inputClasses, 'rounded-[10px] border-r')}
      />
    </div>
  );
}

/**
 * Filter bar for the merged shipments view: method/status selects +
 * shipment number/reference search groups + Search button on one row, then
 * an advanced toggle (detailed status, tag, order number/email/phone,
 * order & shipment date ranges, "only without lockers").
 */
export function ShipmentsFilters({
  values,
  onApply,
}: {
  values: ShipmentsFilterValues;
  onApply: (values: ShipmentsFilterValues) => void;
}) {
  const { t } = useTranslation('shipments');
  const [draft, setDraft] = useState<ShipmentsFilterValues>(values);
  const [showAdvanced, setShowAdvanced] = useState(
    Boolean(
      values.date_from ||
      values.date_to ||
      values.shipment_date_from ||
      values.shipment_date_to ||
      values.event_code ||
      values.tag ||
      values.order_number ||
      values.email ||
      values.phone ||
      values.missing_point,
    ),
  );

  const set = <K extends keyof ShipmentsFilterValues>(key: K, value: ShipmentsFilterValues[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  const apply = () => onApply(draft);

  return (
    <div className="flex flex-col gap-[29px]">
      <div className="flex flex-col gap-4">
        <div className={filterGridClasses}>
          <div className="flex flex-col gap-3">
            <span className={fieldLabelClasses}>{t('filters.deliveryMethod')}</span>
            <Select
              value={draft.service_type ?? FILTER_ANY_VALUE}
              onValueChange={(v) =>
                set('service_type', v === FILTER_ANY_VALUE ? null : (v as ServiceType))
              }
            >
              <SelectTrigger className={selectTriggerClasses}>
                <SelectValue placeholder={t('filters.anyMethod')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={FILTER_ANY_VALUE}>{t('filters.anyMethod')}</SelectItem>
                {SERVICE_TYPES.map((v) => (
                  <SelectItem key={v} value={v}>
                    {t(`serviceTypes.${v}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-3">
            <span className={fieldLabelClasses}>{t('filters.status')}</span>
            <Select
              value={draft.status ?? FILTER_ANY_VALUE}
              onValueChange={(v) => {
                // The group status and the detailed-status drill-down are mutually
                // exclusive - picking a group clears any specific event code.
                const next = v === FILTER_ANY_VALUE ? null : (v as OrderStatusFilter);
                setDraft((prev) => ({
                  ...prev,
                  status: next,
                  event_code: next ? null : prev.event_code,
                }));
              }}
            >
              <SelectTrigger className={selectTriggerClasses}>
                <SelectValue placeholder={t('filters.anyStatus')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={FILTER_ANY_VALUE}>{t('filters.anyStatus')}</SelectItem>
                {ORDER_STATUS_FILTERS.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s === ORDER_SHIPMENT_STATUS_NEW
                      ? t('statusGroups.new')
                      : t(`statusGroups.${s}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <SearchInputGroup
            label={t('filters.shipmentNumber')}
            ariaLabel={t('filters.submit')}
            value={draft.tracking_number}
            onChange={(v) => set('tracking_number', v)}
            onSubmit={apply}
          />

          <SearchInputGroup
            label={t('filters.reference')}
            ariaLabel={t('filters.submit')}
            value={draft.reference}
            onChange={(v) => set('reference', v)}
            onSubmit={apply}
          />
        </div>

        {showAdvanced ? (
          // gap-4 everywhere (with gap-y-4 inside the wrapping grids) so every
          // filter row sits at the same vertical rhythm - a phone field wrapping
          // to its own grid line must not open a wider gap than the row above.
          <div className="flex flex-col gap-4">
            <div className="grid gap-x-8 gap-y-4 md:grid-cols-2 xl:grid-cols-4">
              <div className="flex flex-col gap-3">
                <span className={fieldLabelClasses}>{t('filters.detailedStatus')}</span>
                <DetailedStatusCombobox
                  value={draft.event_code}
                  onChange={(v) =>
                    // Picking a specific detailed status clears the coarser group filter.
                    setDraft((prev) => ({ ...prev, event_code: v, status: v ? null : prev.status }))
                  }
                />
              </div>
              <TextFilterGroup
                label={t('filters.tag')}
                value={draft.tag}
                onChange={(v) => set('tag', v)}
                onSubmit={apply}
                placeholder={t('filters.tagPlaceholder')}
              />
              <TextFilterGroup
                label={t('filters.orderNumber')}
                value={draft.order_number}
                onChange={(v) => set('order_number', v)}
                onSubmit={apply}
              />
              <TextFilterGroup
                label={t('filters.email')}
                value={draft.email}
                onChange={(v) => set('email', v)}
                onSubmit={apply}
              />
              <TextFilterGroup
                label={t('filters.phone')}
                value={draft.phone}
                onChange={(v) => set('phone', v)}
                onSubmit={apply}
              />
            </div>

            <div className="grid gap-x-8 gap-y-4 md:grid-cols-2 xl:grid-cols-4">
              <div className="flex flex-col gap-3">
                <span className={fieldLabelClasses}>{t('filters.orderDateFrom')}</span>
                <DatePicker
                  value={draft.date_from || undefined}
                  onChange={(v) => set('date_from', v ?? '')}
                  placeholder={t('filters.pickDate')}
                  aria-label={t('filters.orderDateFrom')}
                />
              </div>
              <div className="flex flex-col gap-3">
                <span className={fieldLabelClasses}>{t('filters.orderDateTo')}</span>
                <DatePicker
                  value={draft.date_to || undefined}
                  onChange={(v) => set('date_to', v ?? '')}
                  placeholder={t('filters.pickDate')}
                  aria-label={t('filters.orderDateTo')}
                />
              </div>
              <div className="flex flex-col gap-3">
                <span className={fieldLabelClasses}>{t('filters.shipmentDateFrom')}</span>
                <DatePicker
                  value={draft.shipment_date_from || undefined}
                  onChange={(v) => set('shipment_date_from', v ?? '')}
                  placeholder={t('filters.pickDate')}
                  aria-label={t('filters.shipmentDateFrom')}
                />
              </div>
              <div className="flex flex-col gap-3">
                <span className={fieldLabelClasses}>{t('filters.shipmentDateTo')}</span>
                <DatePicker
                  value={draft.shipment_date_to || undefined}
                  onChange={(v) => set('shipment_date_to', v ?? '')}
                  placeholder={t('filters.pickDate')}
                  aria-label={t('filters.shipmentDateTo')}
                />
              </div>
            </div>

            <label className="flex w-fit cursor-pointer items-center gap-2">
              <Switch
                checked={draft.missing_point}
                onCheckedChange={(checked) => set('missing_point', checked === true)}
                aria-label={t('filters.onlyMissingPoint')}
              />
              <span className="text-sm leading-none text-black">
                {t('filters.onlyMissingPoint')}
              </span>
            </label>
          </div>
        ) : null}
      </div>

      <div className="flex items-center justify-between gap-4">
        <button
          type="button"
          onClick={() => setShowAdvanced((v) => !v)}
          className="flex h-9 w-fit items-center gap-2 rounded-[10px] border border-[#e5e5e5] bg-white px-4 text-sm font-medium leading-5 text-foreground hover:bg-accent"
        >
          <Plus className="size-4" />
          {showAdvanced ? t('filters.hideAdvanced') : t('filters.showAdvanced')}
        </button>

        <button type="button" onClick={apply} className={filterSubmitButtonClasses}>
          <Filter className="size-4" />
          {t('filters.submit')}
        </button>
      </div>
    </div>
  );
}
